import type { StateCreator } from 'zustand'
import type { SelectionTarget } from './selectionSlice'
import { trazerParaAFrente } from '@core/layout/camadas'

export interface PropertiesPopup {
  id: string
  target: SelectionTarget
  position: { x: number; y: number }
  size: { w: number; h: number }
}

// Standalone "Info do modelo" floating window. Singleton: there's
// only ever one open at a time (no need for an ID list). Position /
// size persist while the window is open; closing resets to defaults
// on reopen.
export interface ModelInfoPopup {
  open: boolean
  position: { x: number; y: number }
  size: { w: number; h: number }
}

export interface PopupsSlice {
  popups: PropertiesPopup[]
  modelInfoPopup: ModelInfoPopup

  openPopup: (target: SelectionTarget) => string
  closePopup: (id: string) => void
  movePopup: (id: string, position: { x: number; y: number }) => void
  resizePopup: (id: string, size: { w: number; h: number }) => void
  bringPopupToFront: (id: string) => void
  closePopupsForModel: (modelId: string) => void

  setModelInfoOpen: (open: boolean) => void
  moveModelInfoPopup: (position: { x: number; y: number }) => void
  resizeModelInfoPopup: (size: { w: number; h: number }) => void
  bringModelInfoToFront: () => void
}

const POPUP_DEFAULT_SIZE = { w: 420, h: 620 }
const POPUP_SPAWN_OFFSET = 32
const MODEL_INFO_DEFAULT_SIZE = { w: 460, h: 680 }

let popupCounter = 0

/** Os ids das janelas na ordem de camadas.ts (o z-index vem de lá). */
export const idDaJanelaDePropriedades = (id: string) => `propriedades:${id}`
export const ID_JANELA_INFO_DO_MODELO = 'info-do-modelo'

function defaultModelInfoSpawn(): { x: number; y: number } {
  // Top-left bias so the model-info window sits next to the spatial
  // tree (which is on the left in the standard layout) rather than
  // overlapping the right sidebar where properties live.
  return { x: 80, y: 90 }
}

function nextSpawn(existing: PropertiesPopup[]): { x: number; y: number } {
  // Spawn around the viewport centre so the right sidebar (fixed,
  // floating, or wide-resized) never covers the new popup. Cap to
  // the actual viewport so a popup opened from a small window never
  // ends up beyond the visible area.
  const w = POPUP_DEFAULT_SIZE.w
  const h = POPUP_DEFAULT_SIZE.h
  const baseX = Math.max(60, Math.floor(window.innerWidth / 2 - w / 2))
  const baseY = 90
  const n = existing.length
  const x = Math.min(
    Math.max(0, baseX + n * POPUP_SPAWN_OFFSET),
    Math.max(0, window.innerWidth - 80),
  )
  const y = Math.min(
    Math.max(0, baseY + n * POPUP_SPAWN_OFFSET),
    Math.max(0, window.innerHeight - Math.min(h, 200)),
  )
  return { x, y }
}

export const createPopupsSlice: StateCreator<PopupsSlice> = (set, get) => ({
  popups: [],
  modelInfoPopup: {
    open: false,
    position: defaultModelInfoSpawn(),
    size: MODEL_INFO_DEFAULT_SIZE,
  },

  openPopup: (target) => {
    const id = `popup-${++popupCounter}`
    const existing = get().popups
    set({
      popups: [
        ...existing,
        {
          id,
          target,
          position: nextSpawn(existing),
          size: POPUP_DEFAULT_SIZE,
        },
      ],
    })
    trazerParaAFrente(idDaJanelaDePropriedades(id))
    return id
  },
  closePopup: (id) =>
    set((state) => ({ popups: state.popups.filter((p) => p.id !== id) })),
  movePopup: (id, position) =>
    set((state) => ({
      popups: state.popups.map((p) =>
        p.id === id ? { ...p, position } : p,
      ),
    })),
  resizePopup: (id, size) =>
    set((state) => ({
      popups: state.popups.map((p) => (p.id === id ? { ...p, size } : p)),
    })),
  bringPopupToFront: (id) => trazerParaAFrente(idDaJanelaDePropriedades(id)),
  closePopupsForModel: (modelId) =>
    set((state) => ({
      popups: state.popups.filter((p) => p.target.modelId !== modelId),
    })),

  setModelInfoOpen: (open) => {
    // On reopen, bring to front and reset position if it would
    // open offscreen (e.g. user resized window since last close).
    if (open) trazerParaAFrente(ID_JANELA_INFO_DO_MODELO)
    set((state) => ({
      modelInfoPopup: {
        ...state.modelInfoPopup,
        open,
        position: open
          ? clampToViewport(state.modelInfoPopup.position)
          : state.modelInfoPopup.position,
      },
    }))
  },
  moveModelInfoPopup: (position) =>
    set((state) => ({
      modelInfoPopup: { ...state.modelInfoPopup, position },
    })),
  resizeModelInfoPopup: (size) =>
    set((state) => ({
      modelInfoPopup: { ...state.modelInfoPopup, size },
    })),
  bringModelInfoToFront: () => trazerParaAFrente(ID_JANELA_INFO_DO_MODELO),
})

function clampToViewport(p: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.min(Math.max(0, p.x), Math.max(0, window.innerWidth - 80)),
    y: Math.min(Math.max(0, p.y), Math.max(0, window.innerHeight - 40)),
  }
}
